/**
 * Per-stage data helpers: which fields to render summary-table-style, which
 * single field is exposed as the canonical "Edit" affordance for today's
 * minimal-viable HIL panel, and what type that field expects.
 */
import type { PausedStage } from '@/types/enums';

export interface StageEditField {
  /** Path inside output_json that the edit form binds to */
  key: string;
  /** Label shown in the form */
  label: string;
  /** Input control type */
  kind: 'text' | 'textarea' | 'number' | 'select';
  /** Optional options for 'select' */
  options?: string[];
  /** Helper text under the field */
  hint?: string;
}

export interface StageDisplaySpec {
  /** One-line summary of what this stage is for, shown above the data */
  blurb: string;
  /** Bullet rows shown as a quick-read summary above the raw JSON */
  summaryKeys?: string[];
  /** Single editable field surfaced today */
  edit: StageEditField;
}

const EDIT_NOTES: StageEditField = {
  key: 'reviewer_notes',
  label: 'Reviewer notes (optional)',
  kind: 'textarea',
  hint: 'Free-text note attached to this stage approval.',
};

const EDIT_TRANSCRIPTION: StageEditField = {
  key: 'text',
  label: 'OCR transcription (edit to correct before approval)',
  kind: 'textarea',
  hint:
    'Compare against the PDF on the left. Edits land in agent_results.edits_json and the corrected text feeds the Document Agent on approval.',
};

export const STAGE_SPECS: Record<PausedStage, StageDisplaySpec> = {
  document_assistant: {
    blurb:
      'CMP Document Assistant transcribed the uploaded file (OCR + handwriting). Compare the text against the original PDF side-by-side. Approve to feed the Document Agent. ABSOLUTE WARNING is shown if extraction was not 100% complete.',
    summaryKeys: ['page_count', 'coverage_percent', 'extraction_complete'],
    edit: EDIT_TRANSCRIPTION,
  },
  document: {
    blurb:
      'Document Agent extracted foundational metadata from the contract: type, language, parties, sections, key dates, financials, governing law, attachments, and confidence. Review before the Parties agent uses this as context.',
    summaryKeys: ['document_type', 'language', 'document_confidence_score'],
    edit: EDIT_NOTES,
  },
  parties: {
    blurb:
      'Parties Agent identified every party with legal identity, role, obligations, signatories, contacts, and liability cap; plus third parties, guarantors, and relationship type. Review before clause analysis runs.',
    summaryKeys: ['relationship_type', 'parties_confidence_score'],
    edit: EDIT_NOTES,
  },
  clause: {
    blurb:
      'Clause Agent extracted every clause with verbatim text, risk level, obligated/beneficiary parties, and balance assessment. Flagged missing standard clauses. Review before Gate 1 quality check.',
    summaryKeys: ['overall_contract_balance', 'clause_confidence_score'],
    edit: EDIT_NOTES,
  },
  gate_1: {
    blurb:
      'Gate 1 quality check — evaluates the first three agents (Document / Parties / Clause) before Compliance runs. Decisions: PROCEED / PROCEED_WITH_WARNINGS / HUMAN_REVIEW_REQUIRED / RETRY.',
    summaryKeys: ['decision', 'checks_passed', 'gate_confidence'],
    edit: EDIT_NOTES,
  },
  compliance: {
    blurb:
      'Compliance Agent assessed GDPR / data-privacy / jurisdiction / contractual compliance with severity-graded issues and an overall verdict. Review before Signature runs.',
    summaryKeys: [
      'overall_compliance_verdict',
      'overall_compliance_score',
      'compliance_confidence_score',
    ],
    edit: EDIT_NOTES,
  },
  signature: {
    blurb:
      'Signature Agent validated execution: signature blocks, dates, completeness, jurisdictional formalities, and e-signature compliance. Review before Gate 2.',
    summaryKeys: ['overall_signature_status', 'signature_confidence_score'],
    edit: EDIT_NOTES,
  },
  gate_2: {
    blurb:
      'Gate 2 quality check — evaluates Compliance + Signature consistency and contract enforceability before the Final Synthesis gate.',
    summaryKeys: [
      'decision',
      'contract_enforceability_verdict',
      'checks_passed',
      'gate_confidence',
    ],
    edit: EDIT_NOTES,
  },
  gate: {
    blurb:
      'Gate Agent (Final Synthesis) — cross-agent consistency check, risk assessment, top findings, recommendations, and pipeline verdict. Review before the final client report is written.',
    summaryKeys: ['pipeline_verdict', 'human_review_required', 'pipeline_quality_score'],
    edit: EDIT_NOTES,
  },
  orchestrator_bootstrap: {
    blurb:
      'Senior legal analyst final report. 9-section client-facing write-up of the entire pipeline. This is the deliverable to the business reader.',
    summaryKeys: [],
    edit: EDIT_NOTES,
  },
  orchestrator: {
    blurb:
      'Legacy orchestrator stage — preserved for jobs paused in the old 7-stage flow. Approving here advances to the Document stage in the new flow.',
    summaryKeys: ['document_class', 'estimated_complexity'],
    edit: EDIT_NOTES,
  },
};
